/* eslint-disable no-restricted-imports */
import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BusinessStatus } from '../../../mappers/business/toBusinessModel';
import { useOpenDialog } from '../../../routing/ModalRoute';
import { DIALOGS } from '../../../routing/constants';
import {
    canEditBusinessAvatar,
    canSeeBusinessSettings,
    canViewBusinessAsAdmin,
    hasAccessToBusinessStat,
    isBloggerBusiness,
    isBloggerOwner,
    isBusinessOwner,
    isDeclinedAdmin,
} from '../../../utils/accessService';
import { analyticsService, EventSource } from '../../../utils/analyticsService';
import { cropPublicId } from '../../../utils/cropPublicId';
import { formatText } from '../../../utils/format/formatText';
import { useIsMobile } from '../../../utils/getIsMobile';
import { ymTrackSettingsClick, ymTrackStatisticsClick } from '../../../utils/metrika';
import { pluralize } from '../../../utils/pluralize';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useIsAdvertisingCatalogEnabled } from '../../../utils/useIsAdvertisingCatalogEnabled';
import { useTranslation } from '../../../utils/useTranslation';
import { AdvertisingButton } from '../../advertising/AdvertisingButton/AdvertisingButton';
import { Skeleton } from '../../feedback/Skeleton';
import { IconCamera } from '../../icon/IconCamera';
import { IconFollow } from '../../icon/IconFollow';
import { IconSettings } from '../../icon/IconSettings';
import { IconUnfollowChecked } from '../../icon/IconUnfollowChecked';
import { Button } from '../../input/Button';
import { Box } from '../../layout/Box';
import { Flex } from '../../layout/Flex';
import { Avatar } from '../../media/Avatar';
import { Typography } from '../../typography/Typography';
import { TypographyExpander } from '../../typography/TypographyExpander';
import { BlockedStatus } from '../BlockedStatus';
import { BusinessStatistic } from '../BusinessStatistic/BusinessStatistic';
import { ReviewsInfo } from '../ReviewsInfo/ReviewsInfo';
import { StatusBadge } from '../StatusBadge';
import { BUSINESS_COVER_RATIO } from './constants';
import { ContextMenu } from './parts/ContextMenu';
import { Cover } from './parts/Cover';
import { DescriptionStatus } from './parts/DescriptionStatus';
import { Property } from './parts/Property';
import { SiteUrlButton } from './parts/SiteUrl';
import {
    BusinessCardContent,
    BusinessCardRoot,
    BusinessCardRow,
    CatalogButton,
    Properties,
    Title,
} from './styles';
import { BusinessCardProps } from './types';

export const BusinessCard = (props: BusinessCardProps) => {
    const {
        business,
        user,
        children,
        onShare: onBaseShare,
        onSubscribe: onBaseSubscribe,
        onPersistBusinessCover,
        onPersistBusinessAvatar,
        statisticLoading,
        businessStatistic,
        onReport,
        onBadgeClick,
        onCatalogClick,
        cpaButton,
        ...other
    } = props;

    const t = useTranslation();
    const openDialog = useOpenDialog();

    const onShare = useCallback(() => onBaseShare(business), [business, onBaseShare]);
    const onSubscribe = useCallback(() => onBaseSubscribe(business), [business, onBaseSubscribe]);
    const isMobile = useIsMobile();
    const onSettingsClick = useCallback(() => {
        ymTrackSettingsClick({
            page: 'Профиль бизнеса',
            userLogin: user?.login,
            businessId: user?.profile?.publicId,
        });
    }, [user?.login, user?.profile?.publicId]);

    const onAllStatisticClick = useCallback(() => {
        ymTrackStatisticsClick({
            page: 'Профиль бизнеса',
            userLogin: user?.login,
            businessId: user?.profile?.publicId,
        });
    }, [user?.login, user?.profile?.publicId]);

    const onClickOutClick = useCallback(() => {
        analyticsService.trackClickOut({
            businessId: business.id,
            clickoutUrl: business.links?.site,
            eventSource: EventSource.BUSINESS,
            target: 'business_site',
        });
    }, [business]);

    const onShowSubscriptions = useCallback(() => {
        openDialog(DIALOGS.SUBSCRIPTIONS);
    }, [openDialog]);

    const isAdvertisingCatalogEnabled =
        useIsAdvertisingCatalogEnabled() && isBusinessOwner(business.id, user);

    const isBlogger = isBloggerBusiness(business.type);
    const isSiteUrlOnTop = !isMobile || !business.catalogUrl || isAdvertisingCatalogEnabled;
    const title = isBlogger ? business.displayName : cropPublicId(business.publicId);
    const subtitle = isBlogger ? undefined : business.displayName;
    const isCoverVisible = !isBlogger;

    const enableBusinessReviewsInfo =
        Number(useExperimentFlag('enable_business_reviews_info')) === 1;

    const onReviewClick = useCallback(() => {
        analyticsService.trackReviewClick({
            businessId: business.id,
            clickoutUrl: business.reviewsInfo?.url,
            eventSource: EventSource.BUSINESS,
        });
    }, [business]);

    const avatarContent = (
        <Box position="relative">
            <Avatar
                mt={isBlogger ? 0 : -3.5}
                size={{ xs: 100, sm: 108 }}
                src={business.avatarUrl}
                title={business.displayName}
                border="1px solid var(--divider)"
                cursor={canEditBusinessAvatar(business.id, user) ? 'pointer' : 'default'}
                onClick={
                    canEditBusinessAvatar(business.id, user) ? onPersistBusinessAvatar : undefined
                }
            />
            {canEditBusinessAvatar(business.id, user) && (
                <Box
                    position="absolute"
                    pointerEvents="none"
                    bottom={0}
                    right={0}
                    p={1}
                    borderRadius="50%"
                    background="var(--control-secondary-background)"
                    backdropFilter="blur(var(--blur-radius-l))"
                >
                    <IconCamera size={20} color="var(--control-secondary-color)" />
                </Box>
            )}
        </Box>
    );

    return (
        <BusinessCardRoot {...other}>
            {isCoverVisible && business.__type === 'standard' ? (
                <Cover
                    business={business}
                    user={user}
                    onPersistBusinessCover={onPersistBusinessCover}
                    onReport={onReport}
                />
            ) : isCoverVisible ? (
                <Skeleton variant="rect" ratio={BUSINESS_COVER_RATIO} />
            ) : null}
            <BusinessCardContent>
                <Box pb={0.75} pt={isBlogger ? 1 : 0}>
                    <BusinessCardRow
                        spacing={{ xs: 1, sm: 2 }}
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ sm: 'center' }}
                        justifyContent={{ sm: 'space-between' }}
                    >
                        {isMobile ? null : avatarContent}
                        <Flex
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            width="100%"
                            overflow={isMobile ? 'visible' : 'hidden'}
                        >
                            {isMobile ? avatarContent : null}
                            <Flex overflow="hidden">
                                <Box>
                                    <Title>{title}</Title>
                                    <Typography
                                        variant="caption"
                                        color="secondary"
                                        pb={{ xs: 0.5, sm: 1 }}
                                    >
                                        {subtitle}
                                    </Typography>
                                </Box>
                                {business.status ? (
                                    <StatusBadge
                                        businessStatus={business.status}
                                        statusDisplay={business.statusDisplay}
                                        onClick={onBadgeClick}
                                    />
                                ) : null}
                                {enableBusinessReviewsInfo && business.reviewsInfo ? (
                                    <ReviewsInfo
                                        {...business.reviewsInfo}
                                        onClick={onReviewClick}
                                        pt={business.status ? { xs: 0.5, sm: 1 } : undefined}
                                    />
                                ) : null}
                                {cpaButton}
                            </Flex>
                            {!isCoverVisible && (
                                <ContextMenu
                                    isCoverVisible={isCoverVisible}
                                    business={business}
                                    user={user}
                                    onReport={onReport}
                                />
                            )}
                        </Flex>
                        <Properties>
                            <Property
                                value={business.posts ?? 0}
                                displayName={pluralize(
                                    business.posts ?? 0,
                                    'пост',
                                    'поста',
                                    'постов',
                                )}
                            />
                            {business.subscribers ? (
                                <Property
                                    value={business.subscribers ?? 0}
                                    displayName={pluralize(
                                        business.subscribers ?? 0,
                                        'подписчик',
                                        'подписчика',
                                        'подписчиков',
                                    )}
                                />
                            ) : null}
                            {isBloggerOwner(business, user) && (
                                <Box role="button" onClick={onShowSubscriptions} cursor="pointer">
                                    <Property
                                        value={user?.subscriptions ?? 0}
                                        displayName={pluralize(
                                            user?.subscriptions ?? 0,
                                            'подписка',
                                            'подписки',
                                            'подписок',
                                        )}
                                    />
                                </Box>
                            )}
                        </Properties>
                    </BusinessCardRow>
                    {business.status === BusinessStatus.DECLINED && (
                        <BlockedStatus login={user?.login} />
                    )}
                    {hasAccessToBusinessStat(business.id, user) && (
                        <BusinessStatistic
                            statistic={businessStatistic?.stats}
                            statisticUrl={business.statisticUrl}
                            onAllStatisticClick={onAllStatisticClick}
                            statisticLoading={statisticLoading}
                        />
                    )}
                    {business.description ? (
                        <BusinessCardRow display="block">
                            {business.descriptionStatus ? (
                                <DescriptionStatus status={business.descriptionStatus} />
                            ) : null}
                            <TypographyExpander
                                width="100%"
                                color="primary"
                                lines={canViewBusinessAsAdmin(business.id, user) ? 2 : 4}
                                backgroundColor="var(--background-primary)"
                            >
                                {formatText(business.description)}
                            </TypographyExpander>
                        </BusinessCardRow>
                    ) : null}
                    <BusinessCardRow
                        direction="row"
                        spacing={1}
                        flexWrap={isAdvertisingCatalogEnabled ? 'wrap' : 'nowrap'}
                    >
                        {isAdvertisingCatalogEnabled ? (
                            <AdvertisingButton
                                flexGrow={1}
                                width={{ xs: 'auto', sm: 'calc(50% - 4px)' }}
                                target="_self"
                            />
                        ) : null}
                        {business.catalogUrl ? (
                            <CatalogButton
                                business={business}
                                onClick={onCatalogClick}
                                outlineIcon
                                width={
                                    isAdvertisingCatalogEnabled
                                        ? { xs: '40px', sm: 'calc(50% - 4px)' }
                                        : 'auto'
                                }
                                flexGrow={isAdvertisingCatalogEnabled ? 0 : 1}
                                hideText={isMobile ? isAdvertisingCatalogEnabled : false}
                            />
                        ) : null}
                        {isSiteUrlOnTop ? (
                            <SiteUrlButton
                                business={business}
                                onClickOutClick={onClickOutClick}
                                onShare={onShare}
                                isBlogger={isBlogger}
                                width={isAdvertisingCatalogEnabled ? 'calc(50% - 4px)' : 'auto'}
                            />
                        ) : null}
                        {canSeeBusinessSettings(business.id, user) ? (
                            <Button
                                as={Link}
                                size="m"
                                flexGrow={1}
                                variant="filled"
                                color="secondary"
                                spacing={0.5}
                                to={business.editLink}
                                onClick={onSettingsClick}
                                width={isAdvertisingCatalogEnabled ? 'calc(50% - 4px)' : 'auto'}
                            >
                                <IconSettings size={20} />
                                {t('Настроить')}
                            </Button>
                        ) : null}
                        {!isDeclinedAdmin(user?.role) &&
                        !canSeeBusinessSettings(business.id, user) ? (
                            <Button
                                flexGrow={1}
                                size="m"
                                variant="filled"
                                color={business.subscribed ? 'secondary' : 'primary'}
                                spacing={0.5}
                                onClick={onSubscribe}
                                width={isAdvertisingCatalogEnabled ? 'calc(50% - 4px)' : 'auto'}
                            >
                                {business.subscribed ? (
                                    <IconUnfollowChecked fontSize={20} />
                                ) : (
                                    <IconFollow fontSize={20} />
                                )}
                                {business.subscribed ? t('Вы подписаны') : t('Подписаться')}
                            </Button>
                        ) : null}
                    </BusinessCardRow>

                    {!isSiteUrlOnTop && (
                        <BusinessCardRow direction="row">
                            {
                                <SiteUrlButton
                                    business={business}
                                    onClickOutClick={onClickOutClick}
                                    onShare={onShare}
                                    isBlogger={isBlogger}
                                />
                            }
                        </BusinessCardRow>
                    )}
                </Box>
                {children}
            </BusinessCardContent>
        </BusinessCardRoot>
    );
};
